// src/RetirementCalculator.js
import React, { useState } from 'react';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { Pie } from 'react-chartjs-2';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';

// Register necessary components
ChartJS.register(
  ArcElement,
  Tooltip,
  Legend
);

const RetirementCalculator = () => {
  const [age, setAge] = useState(25);
  const [monthlySpend, setMonthlySpend] = useState(250);
  const [retirementStyle, setRetirementStyle] = useState("LIKE A KING");
  const [savingStyle, setSavingStyle] = useState("SAFE");
  const [amountRequired, setAmountRequired] = useState(0);
  const [monthlySavings, setMonthlySavings] = useState(0);

  const calculateRetirement = () => {
    // Assuming some basic calculations for example purposes
    const yearlySpend = monthlySpend * 12;
    const retirementFactor = retirementStyle === "LIKE A KING" ? 2 : retirementStyle === "I AM HAPPY THE WAY I AM" ? 1.5 : 1;
    const savingFactor = savingStyle === "SAFE" ? 0.05 : 0.1;
    const yearsToRetirement = 60 - age;

    const totalAmountRequired = yearlySpend * retirementFactor * yearsToRetirement / savingFactor;
    const monthlySavingsRequired = totalAmountRequired / (yearsToRetirement * 12);

    setAmountRequired(totalAmountRequired);
    setMonthlySavings(monthlySavingsRequired);
  };

  const handleInputChange = (setter) => (e) => {
    setter(e.target.value === '' ? 0 : parseInt(e.target.value));
  };

  const handleRetirementStyleChange = (e) => {
    setRetirementStyle(e.target.value);
  };

  const handleSavingStyleChange = (e) => {
    setSavingStyle(e.target.value);
  };

  const data = {
    labels: ['Amount Required', 'Monthly Savings'],
    datasets: [
      {
        label: 'Retirement Savings Breakdown',
        data: [amountRequired, monthlySavings * 12 * (60 - age)],
        backgroundColor: ['#36A2EB', '#FF6384'],
      },
    ],
  };
function Icon({ id, open }) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={2}
            stroke="currentColor"
            className={`${id === open ? "rotate-180" : ""} h-5 w-5 transition-transform`}
        >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
        </svg>
    );
  }
  
  
    const [activeIndex, setActiveIndex] = useState(0);
  
     
    const faqs = [
      {
        question: 'What is a retirement calculator?',
        answer: 'It’s an online tool that estimates your retirement corpus and savings needs based on your income, expenses, and age.',
      },
      {
        question: 'Who should use a retirement calculator?',
        answer: 'Anyone—salaried, self-employed, or business owners—planning for retirement.',
      },
      {
        question: 'What data do I need to use the calculator?',
        answer: 'Age, retirement age, expenses, current savings, inflation rate, and expected returns.',
      },
      {
        question: 'Is the calculator result 100% accurate?',
        answer: 'It gives a close estimate, but real outcomes may vary with changing financial conditions.',
      },
      {
        question: 'Can I include EPF and PPF in current savings?',
        answer: 'Yes, include all retirement-specific savings.',
      },
      {
        question: 'How often should I update my retirement plan?',
        answer: 'Review it annually or during major life changes.',
      },
      {
        question: 'What if I start planning late?',
        answer: 'You’ll need to save more aggressively or delay retirement.',
      },
      {
        question: 'Is inflation really that impactful?',
        answer: 'Yes, it can double your required corpus over 20–30 years.',
      },
      {
        question: 'What is a good age to start retirement planning?',
        answer: 'Ideally, in your 20s or early 30s.',
      },
      {
        question: 'Does the calculator suggest where to invest?',
        answer: 'No, but it helps you estimate how much you need to save and invest.',
      },
      {
        question: 'What happens if I change my retirement age?',
        answer: 'Your monthly savings target will adjust accordingly.',
      },
      {
        question: 'Can I use it for planning my spouse’s retirement too?',
        answer: 'Yes, create separate calculations for individual needs.',
      },
      {
        question: 'Do I need financial knowledge to use the calculator?',
        answer: 'Not at all. The interface is simple and beginner-friendly.',
      },
      {
        question: 'Can I factor in one-time expenses like travel or weddings?',
        answer: 'Yes, most calculators allow optional future expenses.',
      },
      {
        question: 'Is TheFinMaster’s calculator free to use?',
        answer: 'Yes, it’s completely free and accessible online.',
      },
    ];
    const toggleAccordion = (index) => {
      if (activeIndex === index) {
          // If the clicked question is already expanded, collapse it
          setActiveIndex(null);
      } else {
          // If the clicked question is not expanded, expand it
          setActiveIndex(index);
      };
    }
  return (
    <>
    <Helmet>
         <title>Retirement Planner Calculator India | Retirement Savings Calculator Online | TheFinmaster</title>
        <meta  name='description' content='Retirement Planner - It helps you figure out the amount you need to invest regularly to meet your Financial goal. | TheFinmaster'/>
        <meta name='keywords' content='financial retirement planner, retirement income planner, best online retirement planner, early retirement planner, social security retirement planner, 401k retirement planner, military retirement planner' />
        <link rel="canonical" href="https://thefinmaster.com/financial-tools-calculators/retirement-planning-calculator" />
        <meta name="robots" content="max-snippet:-1, max-image-preview:standard" />
        <meta name="robots" content="max-video-preview:-1" />
      </Helmet>
      <nav className="text-gray-500 text-sm my-4 ml-10" aria-label="Breadcrumb">
      <ol className="list-none p-0 inline-flex">
        <li className="flex items-center">
          <Link to="/" className="text-blue-600 hover:text-blue-800 ">Home</Link>
          <svg className="w-4 h-4 mx-2 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z" clipRule="evenodd" />
          </svg>
        </li>
        <li className="flex items-center">
          <Link to="/financial-tools-calculators" className="text-blue-600 hover:text-blue-800">Financial Tools Calculators</Link>
          <svg className="w-4 h-4 mx-2 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z" clipRule="evenodd" />
          </svg>
        </li>
          <li className="flex items-center">
          <span className="text-gray-500 ">Retirement Calculator</span>
        </li> 
      
      </ol>
    </nav>
    <div className='py-4'>
    <div className="container mx-auto p-4 shadow-xl rounded-md ">
      <h1 className="text-2xl font-bold mb-4">Retirement Calculator</h1>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <div className="mb-4">
            <label className="block text-gray-700 font-semibold">How old are you?</label>
            <div className="flex items-center">
              <input
                type="number"
                value={age}
                onChange={handleInputChange(setAge)}
                className="w-1/2 p-2 border rounded mr-2"
              />
              <span className="text-gray-700">{age} years</span>
            </div>
          </div>
          <div className="mb-4">
            <label className="block text-gray-700 font-semibold">How much do you spend per month?</label>
            <div className="flex items-center">
              <input
                type="number"
                value={monthlySpend}
                onChange={handleInputChange(setMonthlySpend)}
                className="w-1/2 p-2 border rounded mr-2"
              />
              <span className="text-gray-700">₹ {monthlySpend.toLocaleString()}</span>
            </div>
          </div>
          <div className="mb-4">
            <label className="block text-gray-700 font-semibold">What kind of retirement you want?</label>
            <div className="flex items-center">
              <label className="mr-4">
                <input
                  type="radio"
                  value="LIKE A KING"
                  checked={retirementStyle === "LIKE A KING"}
                  onChange={handleRetirementStyleChange}
                  className="mr-2"
                />
                LIKE A KING
              </label>
              <label className="mr-4">
                <input
                  type="radio"
                  value="I AM HAPPY THE WAY I AM"
                  checked={retirementStyle === "I AM HAPPY THE WAY I AM"}
                  onChange={handleRetirementStyleChange}
                  className="mr-2"
                />
                I AM HAPPY THE WAY I AM
              </label>
              <label>
                <input
                  type="radio"
                  value="LIKE A MONK"
                  checked={retirementStyle === "LIKE A MONK"}
                  onChange={handleRetirementStyleChange}
                  className="mr-2"
                />
                LIKE A MONK
              </label>
            </div>
          </div>
          <div className="mb-4">
            <label className="block text-gray-700 font-semibold">Where are you saving for your retirement?</label>
            <div className="flex items-center">
              <label className="mr-4">
                <input
                  type="radio"
                  value="SAFE"
                  checked={savingStyle === "SAFE"}
                  onChange={handleSavingStyleChange}
                  className="mr-2"
                />
                SAFE (PF, FD, ETC)
              </label>
              <label>
                <input
                  type="radio"
                  value="AGGRESSIVE"
                  checked={savingStyle === "AGGRESSIVE"}
                  onChange={handleSavingStyleChange}
                  className="mr-2"
                />
                AGGRESSIVE (MUTUAL FUNDS, EQUITY, ETC)
              </label>
            </div>
          </div>
          <button
            onClick={calculateRetirement}
            className="bg-blue-500 text-white px-4 py-2 rounded"
          >
            Calculate
          </button>
        </div>
        <div>
          <div className="mb-4">
            <h2 className="text-xl font-bold">Results</h2>
            <p>Amount required for retirement: ₹ {amountRequired.toLocaleString()}</p>
            <p>How much do you need to save per month to retire: ₹ {monthlySavings.toLocaleString()}</p>
          </div>
          <div>
            <h2 className="text-xl font-bold">Breakdown</h2>
            <div className="flex justify-center">
              <Pie data={data} options={{ maintainAspectRatio: false }} width={300} height={300} />
            </div>
          </div>
        </div>
      </div>
    </div>
    </div>
    <div className="flex flex-wrap lg:px-6 md:px-6 px-0 py-6" >
    <div className="w-full md:w-2/3 p-4 mx-auto">
          <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
            Secure Your Golden Years: Start Planning Now
          </h1>
          <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2">
            Dreaming of a peaceful and financially secure retirement? It starts with preparation. Retirement planning is not just for those nearing the end of their careers. The earlier you begin, the more comfortable and stress-free your golden years can be. A retirement calculator acts as your guide to estimating how much you need to save and invest for a dignified lifestyle post-retirement.
          </p>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Why Is Retirement Planning Essential?
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Retirement is a phase where regular income stops, but expenses continue. Without proper planning, you risk falling short on funds, leading to compromises on your lifestyle and healthcare. Here are a few reasons why retirement planning is crucial:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>Inflation:</strong> The cost of living increases every year. What costs ₹50,000 today may cost over ₹1 lakh in two decades.</li>
              <li><strong>Longevity:</strong> Life expectancy has improved, meaning your retirement corpus must last longer.</li>
              <li><strong>No Active Income:</strong> You may not have a regular income post-retirement, so your savings must sustain your lifestyle.</li>
              <li><strong>Healthcare Expenses:</strong> Medical costs tend to rise with age, making them a significant post-retirement burden.</li>
            </ul>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Whether you're salaried or self-employed, retirement planning ensures you don’t outlive your savings.
            </p>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              The Role of a Retirement Calculator
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              A retirement calculator is a smart tool designed to help individuals estimate their future financial needs. It considers factors such as current expenses, inflation, life expectancy, and expected returns to determine your retirement corpus and monthly savings required.
            </p>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              With TheFinMaster's retirement calculator, you get a data-driven approach to long-term planning. Instead of guesswork, you have a clear savings and investment target tailored to your profile.
            </p>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Benefits of Using TheFinMaster's Retirement Calculator
            </h1>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>Effortless Savings Target:</strong> The calculator gives you an exact monthly savings requirement to meet your post-retirement lifestyle. This eliminates the need for complex financial modeling or expensive financial consultations.</li>
              <li><strong>Strategic Investment Roadmap:</strong> It helps you chart out potential investment vehicles—be it mutual funds, EPF, PPF, or NPS—based on your risk appetite and time horizon.</li>
              <li><strong>Comparison of Retirement Plans:</strong> Evaluate various pension and retirement schemes offered by insurers, banks, and the government. Understand which one aligns with your financial goals.</li>
              <li><strong>Inclusion of One-Time Expenses:</strong> Plan for special post-retirement spending like vacations, home renovations, or gifting without disrupting your primary savings goal.</li>
              <li><strong>Instant and Accurate Results:</strong> The calculator instantly provides realistic projections, saving you time and enabling faster financial decisions.</li>
            </ul>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              The Power of Compound Interest in Retirement Planning
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Compound interest allows your money to grow exponentially over time. The longer you invest, the more you earn—on both your principal and the accumulated interest. Here’s how compound interest makes a difference:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li>Longer Duration = Larger Corpus</li>
              <li>Small Monthly Savings = Big Future Returns</li>
              <li>Start Early = Stress-Free Retirement</li>
            </ul>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Even a small delay in planning can cost you lakhs in missed compounding benefits.
            </p>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Example Scenario: Retirement Planning in Action
            </h1>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li>Monthly Expenses (Current): ₹40,000</li>
              <li>Current Age: 30 years</li>
              <li>Expected Retirement Age: 60 years</li>
              <li>Life Expectancy: 70 years</li>
              <li>Inflation Rate: 3% annually</li>
              <li>Existing Retirement Savings (EPF, PPF, etc.): ₹2,00,000</li>
              <li>Expected Rate of Return on Investments: 7% annually</li>
            </ul>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <strong>Results:</strong>
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li>Required Retirement Corpus: ₹58.18 Lakhs</li>
              <li>Monthly Savings Target: ₹3,878</li>
            </ul>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              This shows how a disciplined saving habit combined with smart investing can help you achieve your retirement goals effortlessly.
            </p>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              What Makes TheFinMaster’s Calculator Unique?
            </h1>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>User-Centric Design:</strong> Intuitive, responsive, and easy for all age groups</li>
              <li><strong>Accurate Projections:</strong> Uses standard formulas and inflation-adjusted inputs</li>
              <li><strong>Actionable Insights:</strong> Offers savings and investment suggestions</li>
              <li><strong>Instant Output:</strong> No waiting, no complexity—just quick, valuable results</li>
            </ul>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              How to Use TheFinMaster’s Retirement Calculator
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Follow these simple steps to plan your retirement:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li>Enter your current age and expected retirement age.</li>
              <li>Input your existing savings, expected monthly expenses, and inflation rate.</li>
              <li>Select an expected rate of return (based on your investment strategy).</li>
              <li>Get your estimated retirement corpus and monthly savings target instantly.</li>
            </ul>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Key Factors to Consider for Retirement Planning
            </h1>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>Lifestyle Choices:</strong> Will your expenses rise or fall post-retirement?</li>
              <li><strong>Dependents:</strong> Will you be financially supporting family members?</li>
              <li><strong>Healthcare Needs:</strong> Factor in potential medical emergencies.</li>
              <li><strong>Inflation Impact:</strong> Adjust your corpus goal annually for inflation.</li>
              <li><strong>Tax Efficiency:</strong> Choose retirement plans that offer tax benefits under 80C, 80CCD, etc.</li>
            </ul>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Start Early, Retire Peacefully
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              One of the best retirement strategies is to start as early as possible. Even small contributions, when compounded over time, can grow into a substantial retirement fund. Early planning gives you:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li>Flexibility to increase savings later</li>
              <li>The ability to handle market volatility</li>
              <li>Confidence in facing future uncertainties</li>
            </ul>
          </div>

          {/* <h1 className="text-xl font-medium text-gray-700 mt-4">
            “Secure your golden years with TheFinMaster! Use our Retirement Calculator to start planning your stress-free retirement today!”
          </h1> */}
        </div>

        <div className="w-full md:w-1/3 p-4">
          <div className="bg-white shadow-md rounded-lg overflow-hidden">
            <div className="bg-blue-100 px-4 py-2 border-b border-blue-600 ">
              <h2 className="text-lg font-semibold ">Popular Categories</h2>
            </div>
            <div className="p-2 ">
            <ul>
                                <Link to='/financial-tools-calculators/swp-calculator'>
                                    <li className="py-2  hover:bg-slate-100 border-b border-gray-300">SWP Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/mutul-fund-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Mutual Fund Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/sukanya-samriddhi-yojana-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Samriddhi Yojana Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/fd-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">FD Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/rd-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">RD Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/nps-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">NPS Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/hra-exemption-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">HRA Exemption Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/retirement-planning-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Retirement Planning Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/emi-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">EMI Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/simple-interest-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Simple Interest Calculator</li>

                                </Link><Link to='/financial-tools-calculators/compound-interest-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Compound Interest Calculator</li>

                                </Link><Link to='/financial-tools-calculators/nsc-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">NSC Calculator</li>

                                </Link><Link to='/financial-tools-calculators/step-up-sip-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Step Up Sip Calculator</li>

                                </Link><Link to='/financial-tools-calculators/income-tax-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Income Tax Calculator</li>
                                    
                                </Link><Link to='/financial-tools-calculators/home-loan-emi-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Home Loan Emi Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/car-loan-emi-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Car Loan Emi Calculator</li>
                                </Link>
                                {/* {categories.map(category => (
       <Link key={category.id} to={`/category/${category.id}`}>
         <li className="py-2 border-b border-gray-300">{category.name}</li>
       </Link>
     ))} */}
                            </ul>
                        </div>
                    </div>
                    <div className="bg-white shadow-md rounded-lg overflow-hidden mt-4">
                        <div className="bg-blue-100 px-4 py-2 border-b border-blue-600">
                            <h2 className="text-lg font-semibold">More Categories</h2>
                        </div>
                        <div className="p-2">
                        <ul>
                                <Link to='/financial-tools-calculators/gratuity-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Gratuity Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/APY-calculator'>
                                    <li className="py-2 border- hover:bg-slate-100 border-gray-300">APY Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/cagr-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">CAGR Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/gst-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">GST Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/flat-vs-reducing-rate-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Flat Vs Reducing Rate Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/equity-margin-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Equity Margin Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/inflation-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Inflation Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/salary-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Salary Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/brokerage-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Brokerage Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/tds-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">TDS Calculator</li>
                                </Link>
                {/* {moreCategories.map(category => (
          <Link key={category.id} to={`/category/${category.id}`}>
            <li className="py-2 border-b border-gray-300">{category.name}</li>
          </Link>
        ))} */}
              </ul>
            </div>
          </div>

        </div>

      </div>
      <div className="max-w-4xl mx-auto lg:px-6 md:px-6 px-2 py-6">
                <h1 className="text-center text-2xl md:text-2xl lg:text-3xl font-bold mb-8">Frequently Asked Questions</h1>
                <div className="space-y-4">
                    {faqs.map((faq, index) => (
                        <div key={index} className="border rounded-lg overflow-hidden shadow-md">
                            <button
                                onClick={() => toggleAccordion(index)}
                                className="w-full p-4 text-left text-lg font-semibold focus:outline-none flex justify-between items-center"
                            >
                                {faq.question}
                                <Icon id={index} open={activeIndex} />
                            </button>
                            {activeIndex === index && (
                                <div className="p-4 bg-gray-100">
                                    <p className="text-gray-700">{faq.answer}</p>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>
    </>
  );
};

export default RetirementCalculator;
